import type {
    AlertRuleEventsQueryParams,
    AlertRuleQueryParams,
    AlertRuleStats,
    FullAlertRule,
    NewAlertRule,
    WebhookDelivery,
    WebhookDeliveryDetail,
    WebhookDeliveryStats,
    WebhookDeliveryStatus,
    WebhookTestRequest,
    WebhookTestResult,
} from '@/types/alert';
import {
    FlirUploadResponse,
    PaginatedResponse,
    UploadFileResponse,
} from '@/types/responses';
import {
    CameraApiResponse,
    CameraValidationResponse,
    CreateSourceFolderPayload,
    FlirRecordingParameters,
    FolderOwnership,
    QuerySuggestionResponse,
    SourceFolder,
    SourceFolderChildren,
    BulkDeleteResult,
    BulkSelectionPayload,
    SourceFolderListResponse,
    SourceFolderMembershipChanges,
    SourceFolderMembershipPayload,
    SpriteLevel,
    StreamFunctionParams,
    ThumbnailSize,
    UpdateSourceFolderPayload,
} from './types';
import { StreamCallbacks, StreamOptions } from '@/types/stream';
import {
    FullSource,
    SourceQueryParams,
    SourceType,
    UpdateSourcePayload,
} from '@/types/source';
import {
    CreateVehiclePayload,
    UpdateVehiclePayload,
    Vehicle,
    VehicleAnalyticsStats,
    VehicleEventLinkResult,
    VehicleMergeResult,
    VehicleMergeSuggestionsResponse,
    VehicleMergeTarget,
    VehicleOccurrence,
    VehiclePaginatedResponse,
    VehicleQueryParams,
    LinkVehiclePayload,
    MergeVehiclesPayload,
} from '@/types/vehicle';
import {
    EventLinkResult,
    LinkPersonPayload,
    MergePersonsPayload,
    MergeResult,
    MergeSuggestionsResponse,
    MergeTarget,
    Person,
    PersonAnalyticsStats,
    PersonOccurrence,
    PersonPaginatedResponse,
    PersonQueryParams,
    PreparedProfilePicture,
    ProfilePictureCropRect,
    UpdatePersonPayload,
} from '@/types/person';
import type { DirectoryStats } from '@/types/common';
import { fetchStream, processStream, isAbortError } from '@/utils/query-stream';
import { enrichSource, enrichSources } from '@/utils/source';
import { toUtcTimestamp } from '@/utils/date';
import { type Method } from 'axios';
import {
    CreatePromptPayload,
    FullPrompt,
    Prompt,
    PromptAttachmentType,
    PromptFilters,
    PromptType,
    UpdatePromptPayload,
} from '@/types/prompt';
import {
    type AlertRuleEvent,
    type DatabaseEvent,
    type DatabaseEventsQueryParams,
    Event,
    type EventEntities,
    EventsPrompts,
    type ScoredEvent,
    UpdateEventsPromptsPayload,
} from '@/types/event';
import {
    Camera,
    CameraReadOnly,
    CreateCamerasPayload,
    UpdateCameraPayload,
} from '@/types/camera';
import { SystemOverview, SystemVersion } from '@/types/system';
import { FullUser } from '@/types/user';
import { chunk } from 'lodash';
import { engineClient } from '@/services/engine/engine-client';
import { fileNameFromContentDisposition } from '@/utils/file-name';
import { RecordingSegment } from '@/types/timeline';
import { CollectionQueryParams } from '@/types/common';
import type { ProcedureTemplate } from '@/types/procedure';
import type {
    AddReportEventInput,
    AddReportEventsResult,
    CreateReportCustomEventPayload,
    CreateReportPayload,
    Report,
    ReportCustomEvent,
    ReportEventDetail,
    ReportOrderResponse,
    ReportListItem,
    ReportPipelineStatus,
    ReportQueryParams,
    ReportWithEvents,
    ReorderReportEventPayload,
    ReorderReportPromptPayload,
    UpdateReportEventPayload,
    UpdateReportPayload,
} from '@/types/report';
import { ExportDownload, ExportJob, ExportType } from '@/types/export';
import type {
    HdmiClusterState,
    HdmiSetupPayload,
    HdmiViewportPreview,
    V4lDevice,
    V4lFormatOption,
} from '@/types/hdmi';
import Config from '@/config';
import { API_PREFIX, ENDPOINTS } from '@/services/engine/endpoints';
import { MAX_PAGE_SIZE } from '@/services/engine/fetch-all-pages';

type EventsQueryParams = CollectionQueryParams & {
    sourceId?: string[];
    folderId?: string[];
    alertRuleId?: string[];
    classId?: number[];
    startTime?: number;
    endTime?: number;
};

type WebhookDeliveriesQueryParams = CollectionQueryParams & {
    status?: WebhookDeliveryStatus[];
    startTime?: number;
    endTime?: number;
};

type WebhookDeliveryStatsQueryParams = {
    startTime?: number;
    endTime?: number;
};

class EngineService {
    private static getEndpoint<T extends keyof typeof ENDPOINTS>(
        entity: T,
    ): `${typeof API_PREFIX}${(typeof ENDPOINTS)[T]}` {
        return (API_PREFIX +
            ENDPOINTS[
                entity
            ]) as `${typeof API_PREFIX}${(typeof ENDPOINTS)[T]}`;
    }

    static getBaseUrl(): string {
        return Config.VITE_ENGINE_URL + API_PREFIX;
    }

    static getStreamingBaseUrl(): string {
        return Config.VITE_ENGINE_URL + this.getEndpoint('streaming');
    }

    public static async query({
        text,
        sourceIds,
        excludedEventIds = [],
        attachment,
        startTime,
        endTime,
        classIds,
        demo = false,
        streamCallbacks,
        topK = 20,
        minConfidence,
        sceneOnly,
        simulation,
        signal,
    }: {
        text: string;
        sourceIds: string[];
        excludedEventIds?: string[];
        attachment?: { url: string; type?: PromptAttachmentType };
        startTime?: Date;
        endTime?: Date;
        classIds?: number[];
        demo?: boolean;
        streamCallbacks: StreamCallbacks;
        topK?: number;
        minConfidence?: number;
        sceneOnly?: boolean;
        simulation?: boolean;
        signal?: AbortSignal;
    }): Promise<void> {
        const endpoint = this.getEndpoint('query');
        const url = new URL(endpoint, Config.VITE_ENGINE_URL);

        const streamOptions: StreamOptions = {
            method: 'POST' as Method,
            url: url.toString(),
            body: {
                text,
                top_k: topK,
                source_ids: sourceIds,
                exclude_ids: excludedEventIds,
                page: 0,
                ...(attachment && { attachment }),
                type: PromptType.NORMAL,
                ...(startTime && { start_time: toUtcTimestamp(startTime) }),
                ...(endTime && { end_time: toUtcTimestamp(endTime) }),
                ...(classIds && classIds.length > 0 && { class_ids: classIds }),
                demo: demo,
                ...(minConfidence != null && {
                    min_confidence: minConfidence,
                }),
                ...(sceneOnly && { mode: 'scene_only' }),
                ...(simulation && { simulation: true }),
            },
            signal,
        };

        await this.stream({
            options: streamOptions,
            onChunk: streamCallbacks.onChunk,
            onDone: streamCallbacks.onDone,
            onEmptyResponse: streamCallbacks.onEmptyResponse,
            onStart: streamCallbacks.onStart,
        });
    }

    public static async queryContinue({
        cursor,
        topK = 10,
        streamCallbacks,
        signal,
    }: {
        cursor: string;
        topK?: number;
        streamCallbacks: StreamCallbacks;
        signal?: AbortSignal;
    }): Promise<void> {
        const endpoint = `${this.getEndpoint('query')}/continue`;
        const url = new URL(endpoint, Config.VITE_ENGINE_URL);

        const streamOptions: StreamOptions = {
            method: 'POST' as Method,
            url: url.toString(),
            body: {
                cursor,
                top_k: topK,
            },
            signal,
        };

        await this.stream({
            options: streamOptions,
            onChunk: streamCallbacks.onChunk,
            onDone: streamCallbacks.onDone,
            onEmptyResponse: streamCallbacks.onEmptyResponse,
            onStart: streamCallbacks.onStart,
        });
    }

    public static async suggestQuery(
        text: string,
    ): Promise<QuerySuggestionResponse> {
        const endpoint = `${this.getEndpoint('query')}/suggestion`;
        return engineClient.post<QuerySuggestionResponse>(endpoint, { text });
    }

    public static async getSources(
        queryParams?: SourceQueryParams,
    ): Promise<PaginatedResponse<FullSource>> {
        const endpoint = this.getEndpoint('sources');
        const response = await engineClient.get<PaginatedResponse<FullSource>>(
            endpoint,
            queryParams,
        );
        return { ...response, items: enrichSources(response.items) };
    }

    public static async getSourceIds(
        queryParams?: SourceQueryParams,
    ): Promise<string[]> {
        const endpoint = `${this.getEndpoint('sources')}/ids`;
        return engineClient.get<string[]>(endpoint, queryParams);
    }

    public static async getReports(
        queryParams?: ReportQueryParams,
    ): Promise<PaginatedResponse<ReportListItem>> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.get<PaginatedResponse<ReportListItem>>(
            endpoint,
            queryParams as Record<string, unknown>,
        );
    }

    public static async createReport(
        payload: CreateReportPayload,
    ): Promise<Report> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.post<Report>(endpoint, payload);
    }

    public static async updateReport(
        reportId: string,
        payload: UpdateReportPayload,
    ): Promise<Report> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.patch<Report>(`${endpoint}/${reportId}`, payload);
    }

    public static async deleteReports(
        selection: BulkSelectionPayload,
        filters?: ReportQueryParams,
    ): Promise<BulkDeleteResult> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.delete<BulkDeleteResult>(endpoint, {
            data: selection,
            params: filters,
        });
    }

    public static async deleteReport(reportId: string): Promise<void> {
        const endpoint = this.getEndpoint('reports');
        await engineClient.delete(`${endpoint}/${reportId}`);
    }

    public static async getReportWithEvents(
        reportId: string,
        params?: { minConfidence?: number },
    ): Promise<ReportWithEvents> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.get<ReportWithEvents>(`${endpoint}/${reportId}`, {
            minConfidence: params?.minConfidence,
        });
    }

    public static async getReportPipelineStatus(
        reportId: string,
    ): Promise<ReportPipelineStatus> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.get<ReportPipelineStatus>(
            `${endpoint}/${reportId}/status`,
        );
    }

    public static async reorderReportEvent(
        reportId: string,
        payload: ReorderReportEventPayload,
    ): Promise<ReportOrderResponse> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.patch<ReportOrderResponse>(
            `${endpoint}/${reportId}/event-order`,
            payload,
        );
    }

    public static async reorderReportPrompt(
        reportId: string,
        payload: ReorderReportPromptPayload,
    ): Promise<ReportOrderResponse> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.patch<ReportOrderResponse>(
            `${endpoint}/${reportId}/prompt-order`,
            payload,
        );
    }

    public static async getReportEvent(
        eventId: string,
    ): Promise<ReportEventDetail> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.get<ReportEventDetail>(
            `${endpoint}/events/${eventId}`,
        );
    }

    public static async removeReportEvents(
        reportId: string,
        selection: BulkSelectionPayload,
    ): Promise<BulkDeleteResult> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.delete<BulkDeleteResult>(
            `${endpoint}/${reportId}/events`,
            { data: selection },
        );
    }

    public static async removeReportEvent(eventId: string): Promise<void> {
        const endpoint = this.getEndpoint('reports');
        await engineClient.delete(`${endpoint}/events/${eventId}`);
    }

    public static async updateReportEvent(
        eventId: string,
        payload: UpdateReportEventPayload,
    ): Promise<ReportEventDetail> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.patch<ReportEventDetail>(
            `${endpoint}/events/${eventId}`,
            payload,
        );
    }

    public static async createReportCustomEvent(
        reportId: string,
        payload: CreateReportCustomEventPayload,
    ): Promise<ReportCustomEvent> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.post<ReportCustomEvent>(
            `${endpoint}/${reportId}/custom-events`,
            payload,
        );
    }

    public static async addReportEvents(
        reportId: string,
        events: AddReportEventInput[],
    ): Promise<AddReportEventsResult> {
        const endpoint = this.getEndpoint('reports');
        return engineClient.post<AddReportEventsResult>(
            `${endpoint}/${reportId}/events`,
            { events },
        );
    }

    public static async startExport(
        type: ExportType,
        data: Record<string, unknown> = {},
    ): Promise<ExportJob> {
        const endpoint = this.getEndpoint('exports');
        return engineClient.post<ExportJob>(endpoint, { type, data });
    }

    public static async getExportJob(jobId: string): Promise<ExportJob> {
        const endpoint = this.getEndpoint('exports');
        return engineClient.get<ExportJob>(`${endpoint}/${jobId}`);
    }

    public static async downloadExport(jobId: string): Promise<ExportDownload> {
        const endpoint = this.getEndpoint('exports');
        const response = await engineClient.getResponse<Blob>(
            `${endpoint}/${jobId}/download`,
            {
                responseType: 'blob',
            },
        );
        return {
            blob: response.data,
            fileName: fileNameFromContentDisposition(
                response.headers['content-disposition'],
            ),
        };
    }

    public static async getProcedureTemplates(params: {
        page: number;
        size: number;
    }): Promise<PaginatedResponse<ProcedureTemplate>> {
        const endpoint = this.getEndpoint('procedures');
        return engineClient.get<PaginatedResponse<ProcedureTemplate>>(
            `${endpoint}/templates`,
            params,
        );
    }

    public static async getSource(id: string): Promise<FullSource> {
        const endpoint = this.getEndpoint('sources');
        const response = await engineClient.get<FullSource>(
            `${endpoint}/${id}`,
        );
        return enrichSource(response);
    }

    public static async getSourceFolders(params: {
        page: number;
        size: number;
        excludeEmpty?: boolean;
        ownership?: FolderOwnership;
        orderBy?: string;
        search?: string;
    }): Promise<SourceFolderListResponse> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.get<SourceFolderListResponse>(
            endpoint,
            params,
        );
        return response;
    }

    public static async getSourceFolder(id: string): Promise<SourceFolder> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.get<SourceFolder>(
            `${endpoint}/${id}`,
        );
        return response;
    }

    public static async createSourceFolder(
        payload: CreateSourceFolderPayload,
    ): Promise<SourceFolder> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.post<SourceFolder>(
            endpoint,
            payload,
        );
        return response;
    }

    public static async updateSourceFolder(
        id: string,
        payload: UpdateSourceFolderPayload,
    ): Promise<SourceFolder> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.patch<SourceFolder>(
            `${endpoint}/${id}`,
            payload,
        );
        return response;
    }

    public static async deleteSourceFolder(
        id: string,
    ): Promise<{ message: string }> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.delete<{ message: string }>(
            `${endpoint}/${id}`,
        );
        return response;
    }

    public static async getSourceFolderChildren(
        id: string,
        params?: { search?: string; sourceType?: SourceType },
    ): Promise<SourceFolderChildren> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.get<SourceFolderChildren>(
            `${endpoint}/${id}/children`,
            params,
        );
        return response;
    }

    public static async addSourceFolderSources(
        id: string,
        payload: SourceFolderMembershipPayload,
    ): Promise<{ message: string }> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.post<{ message: string }>(
            `${endpoint}/${id}/sources`,
            payload,
        );
        return response;
    }

    public static async updateSourceFolderMemberships(
        payload: SourceFolderMembershipChanges,
        filters?: SourceQueryParams,
    ): Promise<{ message: string }> {
        const endpoint = this.getEndpoint('sourceFolders');
        const response = await engineClient.patch<{ message: string }>(
            `${endpoint}/membership`,
            payload,
            { params: filters },
        );
        return response;
    }

    public static async getSourcesByIds(ids: string[]): Promise<FullSource[]> {
        if (ids.length === 0) return [];
        const responses = await Promise.allSettled(
            chunk(ids, MAX_PAGE_SIZE).map((idsBatch) =>
                this.getSources({ id: idsBatch, size: idsBatch.length }),
            ),
        );
        return responses.flatMap((response) => {
            if (response.status === 'rejected') {
                console.error('Failed to fetch sources batch', response.reason);
                return [];
            }
            return response.value.items;
        });
    }

    public static async deleteSources(
        selection: BulkSelectionPayload,
        filters?: SourceQueryParams,
    ): Promise<BulkDeleteResult> {
        const endpoint = this.getEndpoint('sources');
        return engineClient.delete<BulkDeleteResult>(endpoint, {
            data: selection,
            params: filters,
        });
    }

    public static async deleteSource(id: string): Promise<void> {
        const endpoint = this.getEndpoint('sources');
        await engineClient.delete(`${endpoint}/${id}`);
    }

    public static async updateSource(
        id: string,
        payload: UpdateSourcePayload,
    ): Promise<boolean> {
        const endpoint = this.getEndpoint('sources');
        const response = await engineClient.put<boolean>(
            `${endpoint}/${id}`,
            payload,
        );
        return response;
    }

    public static async startAnalysis(sourceId: string): Promise<void> {
        const endpoint = this.getEndpoint('analyses');
        await engineClient.post<{ sourceId: string }>(endpoint, { sourceId });
    }

    public static async deleteCamera(id: string): Promise<boolean> {
        const endpoint = this.getEndpoint('cameras');
        const response = await engineClient.delete<boolean>(
            `${endpoint}/${id}`,
        );
        return response;
    }

    public static async getAlertRules(
        queryParams?: AlertRuleQueryParams,
    ): Promise<PaginatedResponse<FullAlertRule>> {
        const endpoint = this.getEndpoint('alertRules');
        const response = await engineClient.get<
            PaginatedResponse<FullAlertRule>
        >(endpoint, queryParams);
        return response;
    }

    public static async getAlertUnitCapacity(
        excludeAlertRuleId?: string,
    ): Promise<{
        maxAlertUnits: number | null;
        usedAlertUnits: number;
    }> {
        const endpoint = this.getEndpoint('alertRules');
        return engineClient.get(
            `${endpoint}/capacity`,
            excludeAlertRuleId ? { excludeAlertRuleId } : undefined,
        );
    }

    public static async getAlertRule(id: string): Promise<FullAlertRule> {
        const endpoint = this.getEndpoint('alertRules');
        const response = await engineClient.get<FullAlertRule>(
            `${endpoint}/${id}`,
        );
        return response;
    }

    public static async createAlertRule(
        payload: Partial<NewAlertRule>,
    ): Promise<FullAlertRule> {
        const endpoint = this.getEndpoint('alertRules');
        const response = await engineClient.post<FullAlertRule>(
            endpoint,
            payload,
        );
        return response;
    }

    public static async updateAlertRule(
        id: string,
        payload: Partial<NewAlertRule>,
    ): Promise<FullAlertRule> {
        const endpoint = this.getEndpoint('alertRules');
        const response = await engineClient.put<FullAlertRule>(
            `${endpoint}/${id}`,
            payload,
        );
        return response;
    }

    public static async deleteAlertRule(id: string): Promise<void> {
        const endpoint = this.getEndpoint('alertRules');
        await engineClient.delete(`${endpoint}/${id}`);
    }

    public static async testWebhook(
        request: WebhookTestRequest,
    ): Promise<WebhookTestResult> {
        const endpoint = this.getEndpoint('webhooks');
        return engineClient.post<WebhookTestResult>(
            `${endpoint}/test`,
            request,
        );
    }

    public static async getWebhookDeliveries(
        alertRuleId: string,
        alertRuleActionId: string,
        queryParams?: WebhookDeliveriesQueryParams,
    ): Promise<PaginatedResponse<WebhookDelivery>> {
        const endpoint = `${this.getEndpoint('alertRules')}/${alertRuleId}/actions/${alertRuleActionId}/deliveries`;
        return engineClient.get<PaginatedResponse<WebhookDelivery>>(
            endpoint,
            queryParams,
        );
    }

    public static async getWebhookDelivery(
        alertRuleId: string,
        alertRuleActionId: string,
        deliveryId: string,
    ): Promise<WebhookDeliveryDetail> {
        const endpoint = `${this.getEndpoint('alertRules')}/${alertRuleId}/actions/${alertRuleActionId}/deliveries/${deliveryId}`;
        return engineClient.get<WebhookDeliveryDetail>(endpoint);
    }

    public static async getWebhookDeliveryStats(
        alertRuleId: string,
        alertRuleActionId: string,
        queryParams?: WebhookDeliveryStatsQueryParams,
    ): Promise<WebhookDeliveryStats> {
        const endpoint = `${this.getEndpoint('alertRules')}/${alertRuleId}/actions/${alertRuleActionId}/deliveries/stats`;
        return engineClient.get<WebhookDeliveryStats>(endpoint, queryParams);
    }

    public static async getAlertRuleEvents(
        queryParams?: AlertRuleEventsQueryParams,
    ): Promise<PaginatedResponse<AlertRuleEvent>> {
        const endpoint = `${this.getEndpoint('alertRules')}/alerts`;
        return engineClient.get<PaginatedResponse<AlertRuleEvent>>(
            endpoint,
            queryParams,
        );
    }

    public static async getEvents(
        queryParams?: EventsQueryParams,
    ): Promise<PaginatedResponse<Event>> {
        const endpoint = this.getEndpoint('events');
        return engineClient.get<PaginatedResponse<Event>>(
            endpoint,
            queryParams,
        );
    }

    public static async getDatabaseEvents(
        queryParams?: DatabaseEventsQueryParams,
    ): Promise<PaginatedResponse<DatabaseEvent>> {
        const endpoint = this.getEndpoint('databaseEvents');
        return engineClient.get<PaginatedResponse<DatabaseEvent>>(
            endpoint,
            queryParams,
        );
    }

    public static async searchEvents(
        queryParams?: EventsQueryParams,
    ): Promise<PaginatedResponse<Event>> {
        const endpoint = `${this.getEndpoint('events')}/search`;
        return engineClient.post<PaginatedResponse<Event>>(
            endpoint,
            queryParams ?? {},
        );
    }

    public static async searchSourceEvents(
        sourceId: string,
        queryParams?: Omit<EventsQueryParams, 'sourceId'>,
    ): Promise<PaginatedResponse<Event>> {
        return this.searchEvents({ ...queryParams, sourceId: [sourceId] });
    }

    public static async getAlertRuleAlerts(
        alertRuleId: string,
        queryParams?: AlertRuleEventsQueryParams,
    ): Promise<PaginatedResponse<AlertRuleEvent>> {
        const endpoint = `${this.getEndpoint('alertRules')}/${alertRuleId}/alerts`;
        return engineClient.get<PaginatedResponse<AlertRuleEvent>>(
            endpoint,
            queryParams,
        );
    }

    public static async getAlertRuleStats(
        alertRuleId: string,
        queryParams: { startTime: number; groupBy: string },
    ): Promise<AlertRuleStats> {
        const endpoint = `${this.getEndpoint('alertRules')}/${alertRuleId}/stats`;
        const response = await engineClient.get<AlertRuleStats>(
            endpoint,
            queryParams,
        );
        return response;
    }

    public static async getPrompts(
        filters: PromptFilters,
    ): Promise<PaginatedResponse<FullPrompt>> {
        const endpoint = this.getEndpoint('prompts');
        return engineClient.get<PaginatedResponse<FullPrompt>>(
            endpoint,
            filters,
        );
    }

    public static async createPrompt(
        prompt: CreatePromptPayload,
    ): Promise<FullPrompt> {
        const endpoint = this.getEndpoint('prompts');
        const response = await engineClient.post<FullPrompt>(endpoint, prompt);
        return response;
    }

    public static async updatePrompt(
        id: Prompt['id'],
        payload: UpdatePromptPayload,
    ): Promise<FullPrompt> {
        const endpoint = this.getEndpoint('prompts');
        const response = await engineClient.put<FullPrompt>(
            `${endpoint}/${id}`,
            payload,
        );
        return response;
    }

    public static async deletePrompt(id: Prompt['id']): Promise<boolean> {
        const endpoint = this.getEndpoint('prompts');
        const response = await engineClient.delete<boolean>(
            `${endpoint}/${id}`,
        );
        return response;
    }

    public static async createEventsPrompts(
        eventsPrompts: EventsPrompts,
    ): Promise<EventsPrompts> {
        const endpoint = this.getEndpoint('eventPrompts');
        const response = await engineClient.post<EventsPrompts>(
            endpoint,
            eventsPrompts,
        );
        return response;
    }

    public static async getEvent(
        eventId: Event['id'],
        type?: Event['type'],
    ): Promise<Event> {
        const endpoint = this.getEndpoint('events');
        const response = await engineClient.get<Event>(
            `${endpoint}/${eventId}`,
            {
                type,
            },
        );
        return response;
    }

    public static async getEventEntities(
        eventId: Event['id'],
        type?: Event['type'],
    ): Promise<EventEntities> {
        const endpoint = `${this.getEndpoint('events')}/${eventId}/entities`;
        return engineClient.get<EventEntities>(endpoint, { type });
    }

    public static async searchRelatedEvents(
        eventId: string,
        params?: {
            size?: number;
            threshold?: number;
            sourceId?: string[];
            folderId?: string[];
            excludeIds?: string[];
            startTime?: number;
            endTime?: number;
            classId?: number[];
            orderBy?: string;
        },
    ): Promise<PaginatedResponse<ScoredEvent>> {
        const endpoint = `${this.getEndpoint('events')}/${eventId}/related/search`;
        return engineClient.post<PaginatedResponse<ScoredEvent>>(
            endpoint,
            params ?? {},
        );
    }

    public static async updateEventsPrompts(
        eventId: Event['id'],
        promptId: Prompt['id'],
        payload: UpdateEventsPromptsPayload,
    ): Promise<EventsPrompts> {
        const url = `${this.getEndpoint('eventPrompts')}/${eventId}/${promptId}`;
        const response = await engineClient.put<EventsPrompts>(url, payload);
        return response;
    }

    public static async getPrompt(promptId: Prompt['id']): Promise<FullPrompt> {
        const endpoint = this.getEndpoint('prompts');
        const response = await engineClient.get<FullPrompt>(
            `${endpoint}/${promptId}`,
        );
        return response;
    }

    public static async deleteEvent(eventId: Event['id']): Promise<boolean> {
        const endpoint = this.getEndpoint('events');
        await engineClient.delete(`${endpoint}/${eventId}`);
        return true;
    }

    public static async deleteEvents(
        eventIds: Event['id'][],
    ): Promise<boolean> {
        await Promise.all(eventIds.map((id) => this.deleteEvent(id)));
        return true;
    }

    public static async uploadPromptAttachmentFile(
        file: File,
        onProgress?: (progress: number) => void,
    ): Promise<UploadFileResponse> {
        const endpoint = `${this.getEndpoint('storage')}/prompt_attachments`;
        const body = new FormData();
        body.append('file', file);
        const response = await engineClient.post<UploadFileResponse>(
            endpoint,
            body,
            undefined,
            onProgress,
        );
        return response;
    }

    public static async uploadSource(
        file: File | Blob,
        customFileName?: string,
        onProgress?: (percent: number) => void,
        analysis?: {
            faceRecognition: boolean;
            licensePlateRecognition: boolean;
            opticalCharacterRecognition: boolean;
            processingFps: number;
        },
        reportId?: string | null,
    ): Promise<UploadFileResponse> {
        const endpoint = `${this.getEndpoint('storage')}/sources`;
        const body = new FormData();

        let fileToUpload: File | Blob = file;

        if (customFileName && file instanceof File) {
            fileToUpload = new File([file], customFileName, {
                type: file.type,
            });
        }

        body.append('file', fileToUpload);

        if (analysis) {
            body.append(
                'config',
                JSON.stringify({
                    face_recognition: analysis.faceRecognition,
                    license_plate_recognition: analysis.licensePlateRecognition,
                    optical_character_recognition:
                        analysis.opticalCharacterRecognition,
                    processing_fps: analysis.processingFps,
                }),
            );
        }

        if (reportId) {
            body.append('report_id', reportId);
        }

        return await engineClient.post<UploadFileResponse>(
            endpoint,
            body,
            undefined,
            onProgress,
        );
    }

    public static async createFlirRecording(
        parameters: FlirRecordingParameters,
    ): Promise<FlirUploadResponse> {
        const endpoint = this.getEndpoint('flir');
        const response = await engineClient.post<FlirUploadResponse>(
            `${endpoint}/upload`,
            {
                camera_guid: parameters.cameraGuid,
                device_id: parameters.deviceId,
                start_time: (parameters.startTime as Date).getTime() / 1000,
                end_time: (parameters.endTime as Date).getTime() / 1000,
            },
        );
        return response;
    }

    public static async createCameras(
        payload: CreateCamerasPayload,
    ): Promise<Camera[]> {
        const endpoint = `${this.getEndpoint('sources')}/cameras`;
        const body = payload.map((camera) => ({
            name: camera.name,
            stream_url: camera.streamUrl,
            username: camera.username || null,
            password: camera.password || null,
            config: {
                face_recognition: camera.faceRecognition,
                license_plate_recognition: camera.licensePlateRecognition,
                optical_character_recognition:
                    camera.opticalCharacterRecognition,
                processing_fps: camera.analysisFramesPerSecond,
            },
        }));
        return engineClient.post<CameraApiResponse[]>(endpoint, body);
    }

    public static async validateCamera(
        streamUrl: string,
        username?: string,
        password?: string,
    ): Promise<CameraValidationResponse> {
        const endpoint = `${this.getEndpoint('sources')}/validate`;
        const body = {
            source_path: streamUrl,
            username: username,
            password: password,
        };

        const response = await engineClient.post<CameraValidationResponse>(
            endpoint,
            body,
        );
        return response;
    }

    public static async validateCameraBySourceId(
        sourceId: string,
        overrides?: {
            stream_url?: string;
            username?: string;
            password?: string;
        },
    ): Promise<CameraValidationResponse> {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/validate`;
        return await engineClient.post<CameraValidationResponse>(
            endpoint,
            overrides ?? {},
        );
    }

    public static async getCameras(
        queryParams?: SourceQueryParams,
    ): Promise<PaginatedResponse<CameraReadOnly>> {
        const endpoint = `${this.getEndpoint('sources')}/cameras`;
        return await engineClient.get<PaginatedResponse<CameraReadOnly>>(
            endpoint,
            queryParams,
        );
    }

    public static async getCameraBySourceId(
        sourceId: string,
    ): Promise<CameraReadOnly> {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/camera`;
        return await engineClient.get<CameraReadOnly>(endpoint);
    }

    public static async updateCamera(
        sourceId: string,
        payload: UpdateCameraPayload,
    ): Promise<void> {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/camera`;
        await engineClient.patch(endpoint, {
            name: payload.name,
            stream_url: payload.streamUrl,
            username: payload.username,
            password: payload.password,
        });
    }

    public static async getCameraRecordings(
        sourceId: string,
        startTime: number,
        endTime: number,
        token?: string,
    ): Promise<RecordingSegment[]> {
        const endpoint = `${this.getEndpoint('streaming')}/recordings/${sourceId}`;
        const params: Record<string, any> = {
            after: startTime,
            before: endTime,
        };

        if (token) {
            params.token = token;
        }

        const response = await engineClient.get<RecordingSegment[]>(
            endpoint,
            params,
        );
        return response;
    }

    public static getOrganizationLogoUrl(): string {
        return `${Config.VITE_ENGINE_URL}${API_PREFIX}/organization/logo`;
    }

    public static getSourceThumbnailUrl(
        sourceId: string,
        size: ThumbnailSize = ThumbnailSize.MQ,
    ): string {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/media/thumbnail`;
        const url = new URL(Config.VITE_ENGINE_URL + endpoint);
        url.searchParams.set('size', size);
        return url.toString();
    }

    public static buildSourceFrameThumbnailUrl(
        sourceId: string,
        params: {
            time: number;
            bbox: [number, number, number, number];
            width?: number;
            height?: number;
        },
    ): string {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/media/frame-thumbnail`;
        const url = new URL(Config.VITE_ENGINE_URL + endpoint);
        url.searchParams.set('time', String(params.time));
        const [x1, y1, x2, y2] = params.bbox;
        url.searchParams.set('x1', String(x1));
        url.searchParams.set('y1', String(y1));
        url.searchParams.set('x2', String(x2));
        url.searchParams.set('y2', String(y2));
        if (params.width !== undefined) {
            url.searchParams.set('width', String(params.width));
        }
        if (params.height !== undefined) {
            url.searchParams.set('height', String(params.height));
        }
        return url.toString();
    }

    public static getSourceDownloadUrl(sourceId: string): string {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/download`;
        return Config.VITE_ENGINE_URL + endpoint;
    }

    public static getEventThumbnailUrl(
        eventId: string,
        saveToCache?: boolean,
        width?: number,
        height?: number,
        frameId?: number,
    ): string {
        const endpoint = `${this.getEndpoint('events')}/${eventId}/media/thumbnail`;
        const url = new URL(Config.VITE_ENGINE_URL + endpoint);
        if (saveToCache) {
            url.searchParams.set('save_to_cache', 'true');
        }
        if (width) {
            url.searchParams.set('width', width.toString());
        }
        if (height) {
            url.searchParams.set('height', height.toString());
        }
        if (frameId !== undefined) {
            url.searchParams.set('frame_id', frameId.toString());
        }
        return url.toString();
    }

    public static getSceneThumbnailUrl(
        sceneEventId: string,
        width?: number,
        height?: number,
    ): string {
        const endpoint = `${this.getEndpoint('events')}/${sceneEventId}/media/thumbnail`;
        const url = new URL(Config.VITE_ENGINE_URL + endpoint);
        url.searchParams.set('type', 'SCENE');
        if (width) {
            url.searchParams.set('width', width.toString());
        }
        if (height) {
            url.searchParams.set('height', height.toString());
        }
        return url.toString();
    }

    public static async fetchImageBlob(
        url: string,
        signal?: AbortSignal,
    ): Promise<Blob> {
        return engineClient.get<Blob>(url, undefined, {
            responseType: 'blob',
            signal,
        });
    }

    public static getSourceSpriteSheetUrl(
        sourceId: string,
        level: SpriteLevel,
        index: number,
    ): string {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/media/sprites/${level}/${index}`;
        return Config.VITE_ENGINE_URL + endpoint;
    }

    public static getSourceSpriteVttUrl(
        sourceId: string,
        level: SpriteLevel,
    ): string {
        const endpoint = `${this.getEndpoint('sources')}/${sourceId}/media/sprites/${level}/sprites.vtt`;
        return Config.VITE_ENGINE_URL + endpoint;
    }

    public static async fetchSourceSpriteVtt(
        sourceId: string,
        level: SpriteLevel,
    ): Promise<string> {
        return engineClient.get<string>(
            this.getSourceSpriteVttUrl(sourceId, level),
            undefined,
            { responseType: 'text' },
        );
    }

    public static async getSystemOverview(): Promise<SystemOverview> {
        const endpoint = `${this.getEndpoint('system')}/overview`;
        const response = await engineClient.get<SystemOverview>(endpoint);
        return response;
    }

    public static async getSystemVersion(): Promise<SystemVersion> {
        const endpoint = `${this.getEndpoint('system')}/version`;
        const response = await engineClient.get<SystemVersion>(endpoint);
        return response;
    }

    public static initiateLogin(returnTo?: string | null): void {
        const loginUrl = `${Config.VITE_ENGINE_URL}${this.getEndpoint('auth')}/login/redirect`;
        if (!returnTo) {
            window.location.href = loginUrl;
            return;
        }
        const params = new URLSearchParams({
            return_to: new URL(returnTo, window.location.origin).href,
        });
        window.location.href = `${loginUrl}?${params.toString()}`;
    }

    public static logout(): void {
        window.location.href = `${Config.VITE_ENGINE_URL}${this.getEndpoint('auth')}/logout`;
    }

    public static async checkAuth(): Promise<FullUser | null> {
        try {
            const endpoint = `${this.getEndpoint('auth')}/me`;
            const response = await engineClient.get<FullUser>(endpoint);
            return response;
        } catch (error) {
            console.error('Failed to check auth status', error);
            return null;
        }
    }

    public static async getVehicles(
        queryParams?: VehicleQueryParams,
    ): Promise<VehiclePaginatedResponse> {
        const endpoint = this.getEndpoint('vehicles');
        const response = await engineClient.get<VehiclePaginatedResponse>(
            endpoint,
            queryParams,
        );
        return response;
    }

    public static async getVehiclesStats(): Promise<DirectoryStats> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.get<DirectoryStats>(`${endpoint}/stats`);
    }

    public static async getVehicleTags(): Promise<string[]> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.get<string[]>(`${endpoint}/tags`);
    }

    public static async getPersons(
        queryParams?: PersonQueryParams,
    ): Promise<PersonPaginatedResponse> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<PersonPaginatedResponse>(endpoint, queryParams);
    }

    public static async getPersonsStats(): Promise<DirectoryStats> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<DirectoryStats>(`${endpoint}/stats`);
    }

    public static async getPersonTags(): Promise<string[]> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<string[]>(`${endpoint}/tags`);
    }

    public static async getPerson(id: string): Promise<Person> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<Person>(`${endpoint}/${id}`);
    }

    public static async updatePerson(
        id: string,
        payload: UpdatePersonPayload,
    ): Promise<Person> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.patch<Person>(`${endpoint}/${id}`, payload);
    }

    public static async deletePersons(
        selection: BulkSelectionPayload,
        filters?: Record<string, unknown>,
    ): Promise<BulkDeleteResult> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.delete<BulkDeleteResult>(endpoint, {
            data: selection,
            params: filters,
        });
    }

    public static async deletePerson(id: string): Promise<void> {
        const endpoint = this.getEndpoint('persons');
        await engineClient.delete(`${endpoint}/${id}`);
    }

    public static async preparePersonProfilePicture(
        personId: string,
        file: File,
        onProgress?: (percent: number) => void,
    ): Promise<PreparedProfilePicture> {
        const endpoint = this.getEndpoint('persons');
        const formData = new FormData();
        formData.append('image', file);
        return engineClient.post<PreparedProfilePicture>(
            `${endpoint}/${personId}/profile-picture/prepare`,
            formData,
            undefined,
            onProgress,
        );
    }

    public static async setPersonProfilePicture(
        personId: string,
        pendingId: string,
        crop: ProfilePictureCropRect,
    ): Promise<Person> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.post<Person>(
            `${endpoint}/${personId}/profile-picture`,
            { pendingId, crop },
        );
    }

    public static async removePersonProfilePicture(
        personId: string,
    ): Promise<Person> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.delete<Person>(
            `${endpoint}/${personId}/profile-picture`,
        );
    }

    public static async discardPersonProfilePicture(
        personId: string,
        pendingId: string,
    ): Promise<void> {
        const endpoint = this.getEndpoint('persons');
        await engineClient.delete(
            `${endpoint}/${personId}/profile-picture/pending`,
            { data: { pendingId } },
        );
    }

    public static async getPersonOccurrences(
        personId: string,
        params: {
            page: number;
            size: number;
        },
    ): Promise<PaginatedResponse<PersonOccurrence>> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<PaginatedResponse<PersonOccurrence>>(
            `${endpoint}/${personId}/occurrences`,
            params,
        );
    }

    public static async getPersonStats(
        personId: string,
        queryParams: { startTime: number; groupBy: string },
    ): Promise<PersonAnalyticsStats> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<PersonAnalyticsStats>(
            `${endpoint}/${personId}/stats`,
            queryParams,
        );
    }

    public static async getPersonMergeSuggestions(
        personId: string,
    ): Promise<MergeSuggestionsResponse> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<MergeSuggestionsResponse>(
            `${endpoint}/${personId}/merge-suggestions`,
        );
    }

    public static async searchPersonMergeTargets(params: {
        search: string;
        excludePersonIds: string[];
        page: number;
        size: number;
    }): Promise<PaginatedResponse<MergeTarget>> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.get<PaginatedResponse<MergeTarget>>(
            `${endpoint}/merge-targets`,
            {
                search: params.search,
                excludePersonIds: params.excludePersonIds,
                page: params.page,
                size: params.size,
            },
        );
    }

    public static async mergePersons(
        payload: MergePersonsPayload,
    ): Promise<MergeResult> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.post<MergeResult>(`${endpoint}/merge`, payload);
    }

    public static async relinkPersonEvent(
        personId: string,
        eventId: string,
        payload: LinkPersonPayload,
    ): Promise<EventLinkResult> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.post<EventLinkResult>(
            `${endpoint}/${personId}/events/${eventId}/link`,
            payload,
        );
    }

    public static async attachPersonToEvent(
        eventId: string,
        payload: LinkPersonPayload,
    ): Promise<EventLinkResult> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.post<EventLinkResult>(
            `${endpoint}/events/${eventId}/link`,
            payload,
        );
    }

    public static async detachPersonFromEvent(
        eventId: string,
    ): Promise<EventLinkResult> {
        const endpoint = this.getEndpoint('persons');
        return engineClient.delete<EventLinkResult>(
            `${endpoint}/events/${eventId}/link`,
        );
    }

    public static async attachVehicleToEvent(
        eventId: string,
        payload: LinkVehiclePayload,
    ): Promise<VehicleEventLinkResult> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.post<VehicleEventLinkResult>(
            `${endpoint}/events/${eventId}/link`,
            payload,
        );
    }

    public static async detachVehicleFromEvent(
        eventId: string,
    ): Promise<VehicleEventLinkResult> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.delete<VehicleEventLinkResult>(
            `${endpoint}/events/${eventId}/link`,
        );
    }

    public static async getVehicleMergeSuggestions(
        vehicleId: string,
    ): Promise<VehicleMergeSuggestionsResponse> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.get<VehicleMergeSuggestionsResponse>(
            `${endpoint}/${vehicleId}/merge-suggestions`,
        );
    }

    public static async searchVehicleMergeTargets(params: {
        search: string;
        excludeVehicleIds: string[];
        page: number;
        size: number;
    }): Promise<PaginatedResponse<VehicleMergeTarget>> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.get<PaginatedResponse<VehicleMergeTarget>>(
            `${endpoint}/merge-targets`,
            {
                search: params.search,
                excludeVehicleIds: params.excludeVehicleIds.join(','),
                page: params.page,
                size: params.size,
            },
        );
    }

    public static async mergeVehicles(
        payload: MergeVehiclesPayload,
    ): Promise<VehicleMergeResult> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.post<VehicleMergeResult>(
            `${endpoint}/merge`,
            payload,
        );
    }

    public static async getVehicle(id: string): Promise<Vehicle> {
        const endpoint = this.getEndpoint('vehicles');
        const response = await engineClient.get<Vehicle>(`${endpoint}/${id}`);
        return response;
    }

    public static async createVehicle(
        payload: CreateVehiclePayload,
    ): Promise<Vehicle> {
        const endpoint = this.getEndpoint('vehicles');
        const response = await engineClient.post<Vehicle>(endpoint, payload);
        return response;
    }

    public static async updateVehicle(
        id: string,
        payload: UpdateVehiclePayload,
    ): Promise<Vehicle> {
        const endpoint = this.getEndpoint('vehicles');
        const response = await engineClient.patch<Vehicle>(
            `${endpoint}/${id}`,
            payload,
        );
        return response;
    }

    public static async deleteVehicles(
        selection: BulkSelectionPayload,
        filters?: Record<string, unknown>,
    ): Promise<BulkDeleteResult> {
        const endpoint = this.getEndpoint('vehicles');
        return engineClient.delete<BulkDeleteResult>(endpoint, {
            data: selection,
            params: filters,
        });
    }

    public static async deleteVehicle(id: string): Promise<void> {
        const endpoint = this.getEndpoint('vehicles');
        await engineClient.delete(`${endpoint}/${id}`);
    }

    public static async getVehicleOccurrences(
        vehicleId: string,
        params: {
            page: number;
            size: number;
            eventId?: string;
            startTime?: number;
            endTime?: number;
            orderBy?: string;
        },
    ): Promise<PaginatedResponse<VehicleOccurrence>> {
        const endpoint = this.getEndpoint('vehicles');
        const response = await engineClient.get<
            PaginatedResponse<VehicleOccurrence>
        >(`${endpoint}/${vehicleId}/occurrences`, params);
        return response;
    }

    public static async getVehicleStats(
        vehicleId: string,
        queryParams: { startTime: number; groupBy: string },
    ): Promise<VehicleAnalyticsStats> {
        const endpoint = this.getEndpoint('vehicles');
        const response = await engineClient.get<VehicleAnalyticsStats>(
            `${endpoint}/${vehicleId}/stats`,
            queryParams,
        );
        return response;
    }

    public static isStreamingSource(src: string | undefined): boolean {
        return !!src?.includes(EngineService.getStreamingBaseUrl());
    }

    public static async getHdmiCluster(params?: {
        tenant?: string;
        project?: string;
        cluster?: string;
    }): Promise<HdmiClusterState> {
        const endpoint = this.getEndpoint('hdmi');
        return engineClient.get<HdmiClusterState>(
            `${endpoint}/cluster`,
            params,
        );
    }

    public static async listV4lDevices(): Promise<V4lDevice[]> {
        const endpoint = this.getEndpoint('hdmi');
        return engineClient.get<V4lDevice[]>(`${endpoint}/v4l-devices`);
    }

    public static async listV4lFormats(
        device: string,
    ): Promise<V4lFormatOption[]> {
        const endpoint = this.getEndpoint('hdmi');
        return engineClient.get<V4lFormatOption[]>(`${endpoint}/v4l-formats`, {
            device,
        });
    }

    public static hdmiSnapshotUrl(
        device: string,
        width: number,
        height: number,
        rate: number,
    ): string {
        const endpoint = this.getEndpoint('hdmi');
        const params = new URLSearchParams({
            device,
            width: String(width),
            height: String(height),
            rate: String(rate),
        });
        return `${endpoint}/snapshot?${params.toString()}`;
    }

    public static async previewHdmiViewports(
        payload: HdmiSetupPayload,
    ): Promise<HdmiViewportPreview[]> {
        const endpoint = this.getEndpoint('hdmi');
        return engineClient.post<HdmiViewportPreview[]>(
            `${endpoint}/preview`,
            payload,
        );
    }

    public static async applyHdmi(
        payload: HdmiSetupPayload,
    ): Promise<{ message: string }> {
        const endpoint = this.getEndpoint('hdmi');
        return engineClient.post<{ message: string }>(
            `${endpoint}/apply`,
            payload,
        );
    }

    public static async removeHdmi(params?: {
        tenant?: string;
        project?: string;
        cluster?: string;
    }): Promise<{ message: string }> {
        const endpoint = this.getEndpoint('hdmi');
        return engineClient.post<{ message: string }>(
            `${endpoint}/remove`,
            {},
            { params },
        );
    }

    private static async stream({
        options,
        onChunk,
        onDone,
        onEmptyResponse,
        onStart,
    }: StreamFunctionParams): Promise<void> {
        try {
            const response = await fetchStream(options);
            if (!response || !response.body) return;

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');

            await processStream({
                reader,
                decoder,
                buffer: '',
                chunkIndex: 0,
                onChunk,
                onDone,
                onEmptyResponse,
                onStart,
            });
        } catch (error) {
            if (isAbortError(error)) throw error;
            console.error(`Stream error: ${error}`);
            throw new Error(
                `Stream error: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }
}

export { EngineService };
